import type { ReactNode } from 'react'
import { InformationCircleIcon, ExclamationTriangleIcon, PhotoIcon } from '@heroicons/react/24/outline'
import type { NewsBlock } from '../../api/news'
import { alpha, type SurfaceTokens } from './newsTheme'
import { stripListMarker } from './inlineFormat'

// The ONE block renderer every preview uses (app list/article/home in light
// and dark, and the admin portal), themed by surface tokens. Builds React
// elements only — no HTML strings — so nothing typed in the composer can
// become markup. Inline syntax matches the server: **bold**, [text](https://…).

const INLINE_RE = /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)|\*\*((?:[^*\n]|\*(?!\*))+?)\*\*/g

export function InlineText({ text, linkColor }: { text: string; linkColor: string }) {
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  INLINE_RE.lastIndex = 0
  let k = 0
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    if (m[1] !== undefined) {
      const safe = /^https:\/\//.test(m[2])
      out.push(safe
        ? <a key={k++} href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, fontWeight: 600 }}>{m[1].replace(/\*\*/g, '')}</a>
        : <span key={k++}>{m[1]}</span>)
    } else {
      out.push(<strong key={k++}>{m[3]}</strong>)
    }
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return <>{out}</>
}

export default function BlockRenderer({
  blocks,
  tokens,
  accent,
  compact = false,
}: {
  blocks: NewsBlock[]
  tokens: SurfaceTokens
  /** category colour, for image placeholders */
  accent: string
  compact?: boolean
}) {
  const fs = compact ? 13 : 14
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: fs, lineHeight: 1.5, color: tokens.color }}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading':
            return <h3 key={i} style={{ margin: 0, fontSize: fs + 2, fontWeight: 700, lineHeight: 1.3 }}><InlineText text={b.text} linkColor={tokens.primary} /></h3>
          case 'paragraph':
            return <p key={i} style={{ margin: 0, whiteSpace: 'pre-line' }}><InlineText text={b.text} linkColor={tokens.primary} /></p>
          case 'list': {
            const items = b.items.filter((x) => x.trim())
            // Explicit list-style: Tailwind's preflight sets ol/ul to none, which
            // hid the numbers and bullets the app, portal and email all draw.
            const style = { margin: 0, paddingLeft: 22, listStyleType: b.ordered ? 'decimal' : 'disc', listStylePosition: 'outside' as const }
            const lis = items.map((it, j) => <li key={j} style={{ display: 'list-item', marginTop: j ? 4 : 0, paddingLeft: 2 }}><InlineText text={stripListMarker(it, !!b.ordered)} linkColor={tokens.primary} /></li>)
            return b.ordered ? <ol key={i} style={style}>{lis}</ol> : <ul key={i} style={style}>{lis}</ul>
          }
          case 'callout': {
            const warn = b.tone === 'warn'
            const Icon = warn ? ExclamationTriangleIcon : InformationCircleIcon
            return (
              <div key={i} style={{ borderRadius: 12, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: fs - 0.5, background: warn ? 'rgba(217,119,6,.14)' : 'rgba(37,99,235,.12)' }}>
                <Icon style={{ width: 16, height: 16, flex: 'none', marginTop: 2, color: warn ? '#d97706' : '#2563eb' }} />
                <span><InlineText text={b.text} linkColor={tokens.primary} /></span>
              </div>
            )
          }
          case 'image':
            return b.url
              ? <img key={i} src={b.url} alt={b.alt} style={{ display: 'block', width: '100%', borderRadius: 12, objectFit: 'cover', maxHeight: 220 }} />
              : (
                <div key={i} style={{ borderRadius: 12, height: 110, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 600, background: `linear-gradient(135deg, ${accent}, ${alpha(accent, 0.55)})` }}>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><PhotoIcon style={{ width: 16, height: 16 }} />{b.alt || 'Image'}</span>
                </div>
              )
          case 'button':
            return (
              <button key={i} type="button" style={{ border: 0, borderRadius: 12, padding: 11, fontWeight: 700, fontSize: fs, color: '#fff', background: tokens.primary, width: '100%', cursor: 'default' }}>
                {b.label || 'Button'}
              </button>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
